const MONEY_FORMAT = window.Shopify?.currency?.active
  ? { style: 'currency', currency: window.Shopify.currency.active }
  : null;

/**
 * @param {number} cents
 */
function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents);
  }
  if (MONEY_FORMAT) {
    return new Intl.NumberFormat(undefined, MONEY_FORMAT).format(cents / 100);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

class WcSubscriptionPicker extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;

  /** @type {{ defaultPlanId: number, variants: Record<string, { oneTime: number, plans: Record<string, number> }> } | null} */
  #data = null;

  /** @type {string} */
  #variantId = '';

  /** @type {boolean} */
  #adding = false;

  /** @type {MutationObserver | null} */
  #variantObserver = null;

  connectedCallback() {
    this.#form = this.closest('form');

    const dataEl = this.querySelector('[data-wc-subscription-data]');
    if (dataEl?.textContent) {
      try {
        this.#data = JSON.parse(dataEl.textContent);
      } catch {
        this.#data = null;
      }
    }

    this.#syncVariantId();

    this.querySelectorAll('[data-wc-add]').forEach((button) => {
      button.addEventListener('click', () => this.#handleDirectAdd(button));
    });

    const planSelect = this.querySelector('[data-wc-plan-select]');
    planSelect?.addEventListener('change', () => this.#refreshPrices());

    const variantInput = this.#form?.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', () => this.#refreshPrices());
      this.#variantObserver = new MutationObserver(() => this.#refreshPrices());
      this.#variantObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }

    this.#refreshPrices();
  }

  disconnectedCallback() {
    this.#variantObserver?.disconnect();
    this.#variantObserver = null;
  }

  #syncVariantId() {
    const currentId = this.#form?.querySelector('[name="id"]')?.value || this.dataset.variantId || '';
    if (!currentId || currentId === this.#variantId) return;
    this.#variantId = String(currentId);
    this.dataset.variantId = this.#variantId;
  }

  #activePlanId() {
    const select = this.querySelector('[data-wc-plan-select]');
    return select?.value || this.dataset.defaultPlanId || '';
  }

  /**
   * @param {Element} button
   */
  async #handleDirectAdd(button) {
    if (this.#adding || !(button instanceof HTMLButtonElement)) return;

    const mode = button.dataset.wcAdd;
    if (!mode || !this.#form) return;

    this.#syncVariantId();

    const submitButton = this.#getSubmitButton();
    if (!submitButton || submitButton.disabled) return;

    this.#adding = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      if (mode === 'subscription') {
        this.#setSellingPlan(this.#activePlanId());
      } else {
        this.#clearSellingPlan();
      }

      this.#pushAnalytics('wc_direct_add', {
        mode,
        plan_id: mode === 'subscription' ? this.#activePlanId() : null,
        variant_id: this.#variantId,
      });

      submitButton.click();
    } finally {
      window.setTimeout(() => {
        this.#adding = false;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }, 1200);
    }
  }

  /** @returns {HTMLButtonElement | null} */
  #getSubmitButton() {
    const productForm = this.closest('product-form-component');
    return productForm?.querySelector('button[type="submit"][name="add"]') ?? null;
  }

  /** @param {string} planId */
  #setSellingPlan(planId) {
    if (!this.#form || !planId) return;

    if (!this.#sellingPlanInput) {
      this.#sellingPlanInput = document.createElement('input');
      this.#sellingPlanInput.type = 'hidden';
      this.#sellingPlanInput.name = 'selling_plan';
      this.#form.appendChild(this.#sellingPlanInput);
    }

    this.#sellingPlanInput.value = planId;
    this.#sellingPlanInput.disabled = false;
  }

  #clearSellingPlan() {
    this.#sellingPlanInput?.remove();
    this.#sellingPlanInput = null;
  }

  #refreshPrices() {
    if (!this.#data) return;
    this.#syncVariantId();
    const variantData = this.#data.variants[this.#variantId];
    if (!variantData) return;

    const planId = this.#activePlanId();
    const subCents = variantData.plans[planId];
    const subPriceEl = this.querySelector('[data-wc-sub-price]');
    const oneTimeEl = this.querySelector('[data-wc-onetime-price]');

    if (subPriceEl && subCents) {
      subPriceEl.textContent = formatMoney(subCents);
    }
    if (oneTimeEl) {
      oneTimeEl.textContent = formatMoney(variantData.oneTime);
    }
  }

  /**
   * @param {string} event
   * @param {Record<string, unknown>} payload
   */
  #pushAnalytics(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }
}

if (!customElements.get('wc-subscription-picker')) {
  customElements.define('wc-subscription-picker', WcSubscriptionPicker);
}
